<script setup lang="ts">
import { Form } from 'vee-validate'

const SHOW_CREATE_ACCOUNT = false

const props = withDefaults(
  defineProps<{
    hideTitleOnMobile?: boolean
    hideSignUpOnMobile?: boolean
    module?: 'page' | 'modal'
  }>(),
  {
    hideTitleOnMobile: false,
    hideSignUpOnMobile: false,
    module: 'page',
  },
)

const { $modal, $auth } = useNuxtApp()
const router = useRouter()

const isPage = computed(() => props.module === 'page')

const initialValues = {
  username: '',
  password: '',
}

const handleSuccess = () => {
  if (props.module === 'page') {
    router.push('/')
  } else {
    if ($modal.loginRedirectionPath) {
      router.push($modal.loginRedirectionPath)
    }
    $modal.reset()
  }
}

const handleError = ({ error, setFieldError }) => {
  const { graphQLErrors: errors } = error
  const authenticationError = errors?.some((_error) =>
    ['incorrect_password', 'invalid_username', 'invalid_email'].includes(
      _error.message,
    ),
  )

  if (authenticationError) {
    setFieldError('password', 'Incorrect username/password')
  } else {
    setFieldError('password', errors[0].message)
  }
}

const { form, handleSubmit } = useForm({
  initialValues,
  fetcher: $auth.login,
  onSuccess: handleSuccess,
  onError: handleError,
})
</script>

<template>
  <Form
    v-slot="{ isSubmitting, meta }"
    data-testid="login-form"
    class="fill-max-width max-w-md"
    @submit="handleSubmit"
  >
    <h1
      class="mb-5 text-2xl font-bold"
      :class="{ 'hidden md:block': hideTitleOnMobile }"
    >
      Sign In
    </h1>

    <BaseInput
      v-model="form.username"
      data-testid="username"
      label="Email"
      name="username"
      placeholder="name@email.com"
      rules="required"
      class="mb-1"
      is-focused
      :validate-on-blur="false"
      input-classes="border-none"
    />

    <BaseInput
      v-model="form.password"
      data-testid="password"
      placeholder="********"
      label="Password"
      name="password"
      type="password"
      rules="required"
      class="mb-1"
      input-classes="border-none"
    />

    <div class="text-right">
      <button
        type="button"
        class="text-sm font-semibold underline"
        @click="$modal.open('forgotPassword')"
      >
        Forgotten password?
      </button>
    </div>

    <div
      class="flex flex-col w-full gap-x-6 gap-y-2 flex-wrap mt-5"
      :class="isPage ? 'md:flex-row-reverse' : 'items-center'"
    >
      <BaseButton
        class="!text-sm py-3 !leading-[normal] !px-16 whitespace-nowrap max-w-full"
        :class="isPage ? 'md:w-28' : '!w-full'"
        type="submit"
        :disabled="!meta.valid"
        :loading="isSubmitting"
      >
        Sign In
      </BaseButton>
      <BaseButton
        v-if="SHOW_CREATE_ACCOUNT"
        data-testid="create-account-button"
        class="!text-sm py-3 !leading-[normal] whitespace-nowrap w-full flex-1"
        variant="tertiary"
        @click.prevent="$modal.open('createAccount')"
      >
        Create an Account
      </BaseButton>
    </div>
  </Form>
</template>
